import React, { useEffect, useState } from 'react'
import { X, Loader2 } from 'lucide-react'
import formula1Service from '../services/formula1Service'
import { Formula1RoomSettings } from '../types/formula1'
import { useTranslation } from '../hooks/use-i18n'

interface Props {
  isOpen: boolean
  roomId: string
  onClose: () => void
}

const POSITIONS: Array<{ key: keyof Formula1RoomSettings; label: string }> = [
  { key: 'position1st', label: 'P1' },
  { key: 'position2nd', label: 'P2' },
  { key: 'position3rd', label: 'P3' },
  { key: 'position4th', label: 'P4' },
  { key: 'position5th', label: 'P5' },
  { key: 'position6th', label: 'P6' },
  { key: 'position7th', label: 'P7' },
  { key: 'position8th', label: 'P8' },
  { key: 'position9th', label: 'P9' },
  { key: 'position10th', label: 'P10' },
]

const Formula1RulesModal: React.FC<Props> = ({ isOpen, roomId, onClose }) => {
  const { t } = useTranslation()
  const [settings, setSettings] = useState<Formula1RoomSettings | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!isOpen || !roomId) return
    let cancelled = false
    setLoading(true)
    formula1Service
      .getRoomSettings(roomId)
      .then((data) => {
        if (!cancelled) setSettings(data)
      })
      .catch(() => {
        if (!cancelled) setSettings(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [isOpen, roomId])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="w-full max-w-lg max-h-[85vh] overflow-y-auto glass-strong rounded-2xl p-5 sm:p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-foreground">{t('formula1.rulesTitle')}</h2>
          <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-white/10" aria-label={t('stats.close')}>
            <X className="w-5 h-5" />
          </button>
        </div>
        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <>
            <p className="text-sm text-muted-foreground mb-4">{t('formula1.rulesIntro')}</p>
            <div className="flex flex-wrap gap-2 mb-4">
              {POSITIONS.map((p) => (
                <span key={p.key} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-500/15 border border-white/10 text-sm">
                  <span className="font-bold">{p.label}</span>
                  <span className="text-emerald-300">{settings?.[p.key] ?? '—'}</span>
                </span>
              ))}
            </div>
            <div className="text-sm space-y-1 text-muted-foreground">
              <p>
                {t('formula1.rulesChampionStart')}: <span className="text-foreground font-semibold">{settings?.championAtStart ?? 20}</span>
              </p>
              <p>
                {t('formula1.rulesChampionSecond')}: <span className="text-foreground font-semibold">{settings?.championSecondChance ?? 10}</span>
              </p>
              <p>
                {t('formula1.rulesConstructors')}: <span className="text-foreground font-semibold">{settings?.constructorAtStart ?? 20}</span>
                {' / '}
                <span className="text-foreground font-semibold">{settings?.constructorSecondChance ?? 10}</span>
              </p>
            </div>
            <ul className="mt-4 text-sm text-muted-foreground list-disc pl-5 space-y-1">
              <li>{t('formula1.rulesHow1')}</li>
              <li>{t('formula1.rulesHow2')}</li>
              <li>{t('formula1.rulesHow3')}</li>
            </ul>
          </>
        )}
      </div>
    </div>
  )
}

export default Formula1RulesModal
